import { useState, useEffect, useCallback, useRef } from 'react';
import { executeGetTranslation } from '@services/runApi.service';
import type { RunStageGraphEdge, RunStageGraphVertex } from '@/types/run-stage.types';

/** Indexed map of execute graph edges, keyed as "edgeId|cloneNumber" */
export type ExecuteEdgeMap = Map<string, RunStageGraphEdge>;

/** Indexed map of graph vertices, keyed as "vertexId|cloneNumber" */
export type VertexMap = Map<string, RunStageGraphVertex>;

interface UseExecuteTranslationsResult {
  /** Map of "stepId|cloneNumber" → execute graph edge (actual step state) */
  executeEdgeMap: ExecuteEdgeMap;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetches execute graph edge data for a run and indexes edges by
 * `${edgeId}|${cloneNumber}` for O(1) lookup.
 *
 * These are the EXECUTE graph edges (may differ from plan graph if the user
 * has edited parameters via updateEdgeProperties). Use for the "Actual Step".
 */
export function useExecuteTranslations(runId: string | undefined): UseExecuteTranslationsResult {
  const [executeEdgeMap, setExecuteEdgeMap] = useState<ExecuteEdgeMap>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchCountRef = useRef(0);

  const fetchEdges = useCallback(async () => {
    if (!runId) return;

    const currentCount = ++fetchCountRef.current;
    setLoading(true);
    setError(null);

    try {
      const results = await executeGetTranslation({ runs: [runId] });
      if (currentCount !== fetchCountRef.current) return;

      const map = new Map<string, RunStageGraphEdge>();
      const runResult = results?.[0];

      // The metaData contains the full populated executeGraph
      const meta = runResult?.metaData as
        | {
            executeGraph?: {
              edges?: Array<{
                _id?: string;
                edgeId?: string;
                cloneNumber?: number;
                name?: string;
                subType?: string;
                order?: number;
                creationOrder?: number;
                properties?: Record<string, unknown>;
                checkpoints?: unknown[];
                comments?: string;
              }>;
            };
          }
        | undefined;

      for (const edge of meta?.executeGraph?.edges ?? []) {
        if (!edge.edgeId) continue;
        const key = `${edge.edgeId}|${edge.cloneNumber ?? 0}`;
        map.set(key, edge as RunStageGraphEdge);
      }

      setExecuteEdgeMap(map);
    } catch (err) {
      if (currentCount !== fetchCountRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load execute edges');
    } finally {
      if (currentCount === fetchCountRef.current) setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchEdges();
  }, [fetchEdges]);

  return { executeEdgeMap, loading, error, refresh: fetchEdges };
}

/**
 * Build an edge map from a list of graph edges (plan or execute).
 * Keyed as "edgeId|cloneNumber".
 */
export function buildEdgeMap(edges: RunStageGraphEdge[] | undefined): ExecuteEdgeMap {
  const map = new Map<string, RunStageGraphEdge>();
  for (const edge of edges ?? []) {
    if (!edge.edgeId) continue;
    const key = `${edge.edgeId}|${edge.cloneNumber ?? 0}`;
    map.set(key, edge);
  }
  return map;
}

/** @deprecated Use buildEdgeMap instead */
export const buildPlanEdgeMap = buildEdgeMap;

/**
 * Build a vertex map from a list of graph vertices (plan or execute).
 * Keyed as "vertexId|cloneNumber".
 * Only indexes operation vertices (vertexType === 'operation').
 */
export function buildVertexMap(vertices: unknown[] | undefined): VertexMap {
  const map = new Map<string, RunStageGraphVertex>();
  for (const v of vertices ?? []) {
    const vertex = v as RunStageGraphVertex;
    if (!vertex.vertexId) continue;
    // Only operation vertices contain step parameters
    if (vertex.vertexType && vertex.vertexType !== 'operation') continue;
    const key = `${vertex.vertexId}|${vertex.cloneNumber ?? 1}`;
    map.set(key, vertex);
  }
  return map;
}

